/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description  patient info utils
 */

import _ from 'lodash';
import { formKeys } from 'components/forms/schema/patientInfoSchema';
import { changeDateFormat } from 'utils/dateParser';
import { CUSTOM_ATTRIBUTE_PREFIX } from 'utils/adfUtils';
import { getNameMeta, populateIdentifierMeta } from 'utils/claims/institutionalClaimRequestParser';
import systemMapping from 'app-constants/eligibility/fhirSystemMapping.json';
import { institutionalIdentifierKeyAndCodeMapping as identifierKeyAndCodeMapping } from 'app-constants/claims/claimRequest';
import { EMPTY_VALUE, YEAR_MONTH_DATE_FORMAT } from '@veritable/common/constants';
import { CUSTOM_ATTRIBUTE_FIELD_TYPE } from 'app-constants/admin/customAttribute';

// contact point systems
const contactPointSystems: Record<string, Fhir.ContactPoint['system']> = {
	phone: 'phone',
	email: 'email',
	fax: 'fax',
	other: 'other',
};

// contact point uses
const contactPointUses: Record<string, Fhir.ContactPoint['use']> = {
	home: 'home',
	work: 'work',
	mobile: 'mobile',
};

// get contact info
const getContactInfo = (telecom: Fhir.ContactPoint[]): Patients.IContactInfo => {
	const phoneSystems = _.filter(telecom, { system: contactPointSystems?.phone });
	const email = _.find(telecom, { system: contactPointSystems?.email })?.value;
	const fax = _.find(telecom, { system: contactPointSystems?.fax })?.value;
	const preferredTelecoms = _.filter(telecom, (item) => _.isNumber(item?.rank));
	const preferredContact = _.sortBy(preferredTelecoms, (item) => item?.rank)?.[0]?.system;

	return {
		homePhone: _.find(phoneSystems, { use: contactPointUses?.home })?.value,
		workPhone: _.find(phoneSystems, { use: contactPointUses?.work })?.value,
		cellPhone: _.find(phoneSystems, { use: contactPointUses?.mobile })?.value,
		email,
		fax,
		preferredContact,
	};
};

// get emergency contact
const getEmergencyContact = (contacts: Fhir.PatientContact[]): Patients.IEmergencyContact => {
	const emergencyContact = _.find(contacts || [], (contact) =>
		_.find(contact?.relationship, (contactPoint) => _.find(contactPoint?.coding, (coding) => coding?.code === 'C')),
	) as Fhir.PatientContact;
	return {
		name: getNameMeta([emergencyContact?.name])?.[0]?.fullName,
		address: emergencyContact?.address,
		contactInfo: getContactInfo(emergencyContact?.telecom),
	};
};

// get extension meta
const getExtensionMeta = (patient: Fhir.Patient): Patients.IExtensionMeta => {
	return {
		relationToEmergencyContact: _.find(
			patient?.extension,
			(extension) => extension?.url === systemMapping.patientRelationToEmergencyContact,
		)?.valueString,
	};
};

// get patient identifiers
const getPatientIdentifiers = (patient: Fhir.Patient): Patients.IPatientIdentifiers => {
	const patientMeta: Patients.IPatientIdentifiers = {
		patientAccountNumber:
			_.find(patient?.identifier, (identifier) => identifier?.system === systemMapping.patientControlNumber)?.value || '',
	};
	populateIdentifierMeta({
		mappingConfig: identifierKeyAndCodeMapping?.patient,
		targetMeta: patientMeta,
		identifiers: patient?.identifier,
	});

	return patientMeta;
};

// get preferred language
const getPreferredLanguage = (patient: Fhir.Patient): string => {
	return patient?.communication?.[0]?.language?.coding?.[0]?.code;
};

// get parsed patient info
const getParsedPatientInfo = (patient: Fhir.Patient): Patients.IParsedPatientInfo => {
	if (!patient) {
		return {} as Patients.IParsedPatientInfo;
	}
	return {
		name: getNameMeta(patient?.name)?.[0],
		gender: _.upperFirst(patient?.gender || ''),
		birthDate: patient?.birthDate ? changeDateFormat(patient?.birthDate, YEAR_MONTH_DATE_FORMAT) : '',
		address: patient?.address?.[0],
		contactInfo: getContactInfo(patient?.telecom),
		emergencyContact: getEmergencyContact(patient?.contact),
		extensionMeta: getExtensionMeta(patient),
		patientIdentifiers: getPatientIdentifiers(patient),
		preferredLanguage: getPreferredLanguage(patient),
	};
};

// get first patient identifier
const getFirstPatientIdentifier = (identifiers: Patients.IPatientIdentifiers): InstitutionalClaimParser.IdentifierResponse | null => {
	if (identifiers?.medicalRecordIdentificationNumber?.value) {
		return {
			value: identifiers?.medicalRecordIdentificationNumber?.value,
			display: 'MRN',
		};
	}
	if (identifiers?.patientAccountNumber) {
		return {
			value: identifiers?.patientAccountNumber,
			display: 'Patient Account Number',
		};
	}
	if (identifiers?.memberId?.value) {
		return {
			value: identifiers?.memberId?.value,
			display: 'Member ID',
		};
	}
	if (identifiers?.ssn?.value) {
		return {
			value: identifiers?.ssn?.value,
			display: 'SSN',
		};
	}
	return null;
};

const getDefaultAttributeValue = (fieldType: CustomAttributes.IFieldType): string | boolean | null | string[] => {
	if (fieldType === CUSTOM_ATTRIBUTE_FIELD_TYPE.boolean) {
		return false;
	}
	if (fieldType === CUSTOM_ATTRIBUTE_FIELD_TYPE.date || fieldType === CUSTOM_ATTRIBUTE_FIELD_TYPE.list) {
		return null;
	}
	if (fieldType === CUSTOM_ATTRIBUTE_FIELD_TYPE.multiSelectList) {
		return [];
	}
};

const prepareInitialCustomAttributes = (
	entityCustomAttributeValues?: CustomAttributes.IEntityAttributesValueResponse[],
): Record<string, string> => {
	const initialCustomAttributes = {};
	_.forEach(entityCustomAttributeValues, (value) => {
		initialCustomAttributes[`${CUSTOM_ATTRIBUTE_PREFIX}${value.attributeKey}`] = _.isEmpty(value.attributeValue)
			? getDefaultAttributeValue(value?.attrDef?.props?.fieldType)
			: value.attributeValue;
	});
	return initialCustomAttributes;
};

// get initial model
const getInitialModel = (
	parsedPatientInfo: Patients.IParsedPatientInfo,
	isEditAble: boolean,
	entityCustomAttributeValues?: CustomAttributes.IEntityAttributesValueResponse[],
): Record<string, string> => {
	const personal = formKeys?.personal?.fields;
	const address = formKeys?.address?.fields;
	const contact = formKeys?.contact?.fields;
	const emergencyContact = formKeys?.emergencyContact?.fields;

	const initialCustomAttributes = prepareInitialCustomAttributes(entityCustomAttributeValues);
	const initialModel = {
		[personal.firstName.key]: parsedPatientInfo?.name?.firstName ?? '',
		[personal.middleName.key]: parsedPatientInfo?.name?.middleNames?.[0] ?? '',
		[personal.lastName.key]: parsedPatientInfo?.name?.lastName ?? '',
		[personal.dateOfBirth.key]: parsedPatientInfo?.birthDate ?? '',
		[personal.gender.key]: parsedPatientInfo?.gender ?? '',
		[personal.medicalRecordNumber.key]: parsedPatientInfo?.patientIdentifiers?.medicalRecordIdentificationNumber?.value ?? '',
		[personal.socialSecurityNumber.key]: parsedPatientInfo?.patientIdentifiers?.ssn?.value ?? '',
		[personal.employmentStatus.key]: '',

		[address.addressLine1.key]: parsedPatientInfo?.address?.line?.[0] ?? '',
		[address.addressLine2.key]: parsedPatientInfo?.address?.line?.[1] ?? '',
		[address.city.key]: parsedPatientInfo?.address?.city ?? '',
		[address.state.key]: parsedPatientInfo?.address?.state ?? undefined,
		[address.zipCode.key]: parsedPatientInfo?.address?.postalCode ?? '',

		[contact.homePhone.key]: parsedPatientInfo?.contactInfo?.homePhone ?? '',
		[contact.cellPhone.key]: parsedPatientInfo?.contactInfo?.cellPhone ?? '',
		[contact.workPhone.key]: parsedPatientInfo?.contactInfo?.workPhone ?? '',
		[contact.fax.key]: parsedPatientInfo?.contactInfo?.fax ?? '',
		[contact.emailAddress.key]: parsedPatientInfo?.contactInfo?.email ?? '',
		[contact.preferredLanguage.key]: parsedPatientInfo?.preferredLanguage ?? undefined,
		[contact.preferredContactMethod.key]: parsedPatientInfo?.contactInfo?.preferredContact ?? undefined,

		[emergencyContact.contactName.key]: parsedPatientInfo?.emergencyContact?.name ?? '',
		[emergencyContact.relationToPatient.key]: parsedPatientInfo?.extensionMeta?.relationToEmergencyContact ?? undefined,
		[emergencyContact.addressLine1.key]: parsedPatientInfo?.emergencyContact?.address?.line?.[0] ?? '',
		[emergencyContact.zipCode.key]: parsedPatientInfo?.emergencyContact?.address?.postalCode ?? '',
		[emergencyContact.emailAddress.key]: parsedPatientInfo?.emergencyContact?.address?.line?.[1] ?? '',
		[emergencyContact.homePhone.key]: parsedPatientInfo?.emergencyContact?.contactInfo?.homePhone ?? '',
		[emergencyContact.addressLine2.key]: parsedPatientInfo?.emergencyContact?.address?.line?.[1] ?? '',
		[emergencyContact.city.key]: parsedPatientInfo?.emergencyContact?.address?.city ?? '',
		[emergencyContact.state.key]: parsedPatientInfo?.emergencyContact?.address?.state ?? undefined,
		[emergencyContact.cellPhone.key]: parsedPatientInfo?.emergencyContact?.contactInfo?.cellPhone ?? '',
		[emergencyContact.workPhone.key]: parsedPatientInfo?.emergencyContact?.contactInfo?.workPhone ?? '',
		...initialCustomAttributes,
	};
	if (isEditAble) {
		return initialModel;
	} else {
		return _.mapValues(initialModel, (value) => {
			if (typeof value === 'boolean') {
				return _.upperFirst(_.toString(value));
			} else if (Array.isArray(value)) {
				return value.join(', ');
			}
			return value || EMPTY_VALUE;
		});
	}
};

// create contact point
const createContactPoint = (
	system: Fhir.ContactPoint['system'],
	value: string,
	use?: Fhir.ContactPoint['use'],
	rank?: number,
): Fhir.ContactPoint => {
	if (!value) {
		return null;
	}
	return {
		system,
		value,
		use: use || null,
		rank: rank || null,
	};
};

// prepare submit model
const prepareSubmitModel = (fhirPatient: Fhir.Patient, model: Record<string, string>): Fhir.Patient => {
	const submitModal = _.cloneDeep(fhirPatient);
	const address = formKeys?.address?.fields;
	const contact = formKeys?.contact?.fields;
	const emergencyContact = formKeys?.emergencyContact?.fields;

	const patientAddressLines = _.compact([model?.[address?.addressLine1?.key], model?.[address?.addressLine2?.key]]);

	// patient address
	const patientAddress = [
		{
			line: _.isEmpty(patientAddressLines) ? null : patientAddressLines,
			city: model?.[address?.city?.key] || null,
			state: model?.[address?.state?.key] || null,
			postalCode: model?.[address?.zipCode?.key] || null,
		},
	];
	submitModal.address = patientAddress;

	// patient telecom
	const telecom = _.compact([
		createContactPoint(contactPointSystems?.phone, model?.[contact?.homePhone?.key], contactPointUses?.home),
		createContactPoint(contactPointSystems?.phone, model?.[contact?.cellPhone?.key], contactPointUses?.mobile),
		createContactPoint(contactPointSystems?.phone, model?.[contact?.workPhone?.key], contactPointUses?.work),
		createContactPoint(contactPointSystems?.email, model?.[contact?.emailAddress?.key]),
		createContactPoint(contactPointSystems?.fax, model?.[contact?.fax?.key]),
	]);
	// push the preferred method
	if (model?.[contact?.preferredContactMethod?.key]) {
		telecom.push({
			system: model?.[contact?.preferredContactMethod?.key] as Fhir.ContactPoint['system'],
			rank: 1,
		});
	}
	// push preferred language
	let communication: Fhir.Patient['communication'] = null;
	if (model?.[contact?.preferredLanguage?.key]) {
		communication = [{ language: { coding: [{ code: model?.[contact?.preferredLanguage?.key] }] } }];
	}

	submitModal.telecom = _.isEmpty(telecom) ? null : telecom;
	submitModal.communication = communication;
	// emergency contact
	const emergencyContactAddressLines = _.compact([
		model?.[emergencyContact?.addressLine1?.key],
		model?.[emergencyContact?.addressLine2?.key],
	]);

	const emergencyContactAddress = {
		line: _.isEmpty(emergencyContactAddressLines) ? null : emergencyContactAddressLines,
		city: model?.[emergencyContact?.city?.key] || null,
		state: model?.[emergencyContact?.state?.key] || null,
		postalCode: model?.[emergencyContact?.zipCode?.key] || null,
	};
	const emergencyContactTelecom = _.compact([
		createContactPoint(contactPointSystems?.phone, model?.[emergencyContact?.homePhone?.key], contactPointUses?.home),
		createContactPoint(contactPointSystems?.phone, model?.[emergencyContact?.cellPhone?.key], contactPointUses?.mobile),
		createContactPoint(contactPointSystems.phone, model?.[emergencyContact.workPhone.key], contactPointUses.work),
		createContactPoint(contactPointSystems?.email, model?.[emergencyContact?.emailAddress?.key]),
	]);

	const emergencyContactName = _.split(model?.[emergencyContact.contactName.key], ' ');
	const givenName = _.isEmpty(_.dropRight(emergencyContactName)) ? null : _.dropRight(emergencyContactName);
	const emergencyContactEntity = {
		relationship: [{ coding: [{ code: 'C', system: systemMapping.C }] }],
		name: _.isEmpty(model?.[emergencyContact?.contactName?.key]) ? null : { given: givenName, family: _.last(emergencyContactName) },
		address: emergencyContactAddress,
		telecom: _.isEmpty(emergencyContactTelecom) ? null : emergencyContactTelecom,
	};

	// push relation of emergency contact with patient to patient extension
	const filteredExtension = _.filter(
		fhirPatient?.extension,
		(extension) => extension?.url !== systemMapping.patientRelationToEmergencyContact,
	);
	if (model?.[emergencyContact?.relationToPatient?.key]) {
		filteredExtension.push({
			url: systemMapping.patientRelationToEmergencyContact,
			valueString: model?.[emergencyContact?.relationToPatient?.key],
		});
	}
	submitModal.extension = _.isEmpty(filteredExtension) ? null : filteredExtension;

	// other contacts
	const otherContacts = _.filter(
		fhirPatient?.contact || [],
		(contactItem) =>
			!_.find(contactItem?.relationship, (contactPoint) => _.find(contactPoint?.coding, (coding) => coding?.code === 'C')),
	);

	submitModal.contact = [...(otherContacts || []), emergencyContactEntity];

	return submitModal;
};

export {
	contactPointSystems,
	contactPointUses,
	getContactInfo,
	getParsedPatientInfo,
	getInitialModel,
	prepareSubmitModel,
	getFirstPatientIdentifier,
	createContactPoint,
};
